import { createContext, useContext, useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../firebase/config'
import { normalizePhone } from '../utils/formatUtils'

const AuthContext = createContext()


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState(null)
  // Flag: während des SMS-Registrierungsflows setzt verifyOTPAndRegister den User
  // manuell. Der onAuthStateChanged-Listener würde sonst beim temporären Phone-Auth-User
  // (ohne Firestore-Profil) automatisch signOut triggern und den neu erstellten
  // Email-User killen.
  const isRegistering = useRef(false)

  const applyUser = (userData) => {
    setUser(userData)
    Sentry.setUser({ id: userData.id, email: userData.email, role: userData.role })
  }

  // Bei Auth-State-Änderung Profil aus Firestore laden
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (isRegistering.current) return // SMS-Flow läuft — setUser erfolgt manuell
      if (firebaseUser) {
        const profile = await loadProfile(firebaseUser.uid)
        if (isRegistering.current) return // nach await erneut prüfen
        if (profile) {
          applyUser({ id: firebaseUser.uid, email: firebaseUser.email, ...profile })
        } else {
          // Auth-User ohne Firestore-Profil — Zombie-Account (Registrierung unvollständig)
          await signOut(auth)
          setUser(null)
          setAuthError('Dein Konto konnte nicht vollständig erstellt werden. Bitte erneut registrieren oder den Support kontaktieren.')
        }
      } else {
        setUser(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  // Firestore-Profil laden (admins, companies oder users)
  const loadProfile = async (uid) => {
    // Zuerst Admins prüfen
    const adminSnap = await getDoc(doc(db, 'admins', uid))
    if (adminSnap.exists()) {
      const data = adminSnap.data()
      return { name: data.name, role: 'admin', approved: true }
    }
    // Dann Companies prüfen
    const companySnap = await getDoc(doc(db, 'companies', uid))
    if (companySnap.exists()) {
      const data = companySnap.data()
      return { name: data.name, phone: data.phone, role: 'company', approved: data.approved ?? true, homeAddress: data.homeAddress, serviceCity: data.serviceCity }
    }
    // Dann Users prüfen
    const userSnap = await getDoc(doc(db, 'users', uid))
    if (userSnap.exists()) {
      const data = userSnap.data()
      return { name: data.name, phone: data.phone, role: 'customer', approved: true, homeAddress: data.homeAddress, favoriteAddresses: data.favoriteAddresses || [], onboardingCompleted: data.onboardingCompleted ?? false }
    }
    return null
  }

  // IndexedDB-Persistenz mit Fallback auf Session (iOS ITP / Private Mode)
  const setPersistenceSafe = async (rememberMe) => {
    if (rememberMe) {
      try {
        await setPersistence(auth, browserLocalPersistence)
      } catch {
        await setPersistence(auth, browserSessionPersistence)
      }
    } else {
      await setPersistence(auth, browserSessionPersistence)
    }
  }

  // Einloggen: E-Mail + Passwort
  const login = async (email, password, rememberMe = true) => {
    await setPersistenceSafe(rememberMe)
    const cred = await signInWithEmailAndPassword(auth, email, password)
    const profile = await loadProfile(cred.user.uid)
    if (!profile) throw new Error('Kein Profil gefunden. Bitte kontaktiere den Support.')
    const userData = { id: cred.user.uid, email: cred.user.email, ...profile }
    applyUser(userData)
    return userData
  }

  // Schritt 1 der Kunden-Registrierung: OTP-SMS senden.
  // Gibt das confirmationResult zurück, das der Aufrufer für Schritt 2 braucht.
  const sendPhoneOTP = async (phone, recaptchaVerifier) => {
    const normalized = normalizePhone(phone)
    return await signInWithPhoneNumber(auth, normalized, recaptchaVerifier)
  }

  // Schritt 2 der Kunden-Registrierung: OTP bestätigen + Email/Passwort-Account anlegen.
  // WICHTIG: isRegistering-Flag blockiert den onAuthStateChanged-Listener während
  // des Flows, damit der temporäre Phone-Auth-User keinen Auto-signOut auslöst.
  const verifyOTPAndRegister = async (confirmationResult, otpCode, name, email, phone, password, rememberMe = true) => {
    isRegistering.current = true
    try {
      // 1. OTP bestätigen — erstellt temporär einen Phone-Auth-User
      await confirmationResult.confirm(otpCode)
      // 2. Temporären Phone-User sofort abmelden
      await signOut(auth)
      // 3. Persistenz setzen + Email/Passwort-Account anlegen
      await setPersistenceSafe(rememberMe)
      const normalizedPhone = normalizePhone(phone)
      let cred
      try {
        cred = await createUserWithEmailAndPassword(auth, email, password)
        // 4. Firestore-Profil mit phoneVerifiedAt schreiben
        await setDoc(doc(db, 'users', cred.user.uid), {
          name,
          phone: normalizedPhone,
          email,
          role: 'customer',
          createdAt: serverTimestamp(),
          privacyAcceptedAt: serverTimestamp(),
          agbAcceptedAt: serverTimestamp(),
          phoneVerifiedAt: serverTimestamp(),
          onboardingCompleted: false
        })
      } catch (err) {
        // Auth-User wurde bereits angelegt, aber Firestore-Write schlug fehl →
        // Auth-User löschen, damit kein Zombie-Account entsteht. User kann erneut versuchen.
        if (cred) await cred.user.delete().catch(() => {})
        throw err
      }
      // 5. User-State manuell setzen (Listener ist deaktiviert)
      const userData = {
        id: cred.user.uid,
        email: cred.user.email,
        name,
        phone: normalizedPhone,
        role: 'customer',
        approved: true,
        onboardingCompleted: false
      }
      applyUser(userData)
      setLoading(false)
      return userData
    } finally {
      // Flag mit Delay freigeben, damit gepufferte Listener-Callbacks durchlaufen
      // können, ohne den frisch erstellten User wieder abzumelden.
      setTimeout(() => { isRegistering.current = false }, 500)
    }
  }

  // Registrieren (Unternehmen — muss vom Admin freigeschaltet werden)
  const registerCompany = async (name, email, phone, password, rememberMe = true, serviceCity) => {
    await setPersistenceSafe(rememberMe)
    const normalizedPhone = normalizePhone(phone)
    let cred
    try {
      cred = await createUserWithEmailAndPassword(auth, email, password)
      await setDoc(doc(db, 'companies', cred.user.uid), {
        name,
        phone: normalizedPhone,
        email,
        role: 'company',
        approved: false,
        serviceCity,
        createdAt: serverTimestamp(),
        privacyAcceptedAt: serverTimestamp(),
        agbAcceptedAt: serverTimestamp()
      })
    } catch (err) {
      if (cred) await cred.user.delete().catch(() => {})
      throw err
    }
    const userData = { id: cred.user.uid, name, phone: normalizedPhone, email, role: 'company', approved: false, serviceCity }
    applyUser(userData)
    return userData
  }

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin + '/reset-password',
      handleCodeInApp: true,
    })
  }

  const logout = async () => {
    await signOut(auth)
    setUser(null)
    Sentry.setUser(null)
  }

  const updateProfile = async (name, phone, homeAddress) => {
    if (!user) return
    const updated = { ...user, name, phone, ...(homeAddress !== undefined && { homeAddress }) }
    setUser(updated)
    const col = user.role === 'company' ? 'companies' : 'users'
    const data = { name, phone }
    if (homeAddress !== undefined) data.homeAddress = homeAddress
    await updateDoc(doc(db, col, user.id), data)
  }

  const updateFavoriteAddresses = async (favorites) => {
    if (!user || user.role !== 'customer') return
    const trimmed = favorites.slice(0, 10)
    const previousUser = user
    setUser(prev => ({ ...prev, favoriteAddresses: trimmed }))
    try {
      await updateDoc(doc(db, 'users', user.id), { favoriteAddresses: trimmed })
    } catch (err) {
      setUser(previousUser)
      throw err
    }
  }

  const clearAuthError = () => setAuthError(null)

  return (
    <AuthContext.Provider value={{ user, loading, authError, clearAuthError, login, sendPhoneOTP, verifyOTPAndRegister, registerCompany, resetPassword, logout, updateProfile, updateFavoriteAddresses }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
